import { useEffect, useState } from 'react';
import newsCard1 from './assets/news/news3.jpg';
import newsCard2 from './assets/news/news4.jpg';
import newsCard3 from './assets/news/news7.jpg';

function NewsDetailPage({ newsId, onNavigate }) {
  const [activeImageSlide, setActiveImageSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [newsId]);

  const newsData = {
    'community-involvement': {
      title: 'Community Involvement',
      subtitle: 'Outreach Programs & Service Learning',
      description: 'Highlights of our comprehensive outreach programs, service learning initiatives, and local collaborations led by students and staff.',
      color: '#7c3aed',
      colorLight: '#ede9fe',
      icon: 'fa-handshake',
      images: [newsCard1, newsCard2, newsCard3],
      date: 'March 15, 2026',
      author: 'Communications Team',
      features: [
        {
          icon: 'fa-users',
          title: 'Student Leadership',
          detail: 'Students lead community service projects and mentor younger peers in outreach initiatives.'
        },
        {
          icon: 'fa-heart',
          title: 'Local Partnerships',
          detail: 'Collaborations with local organizations to address community needs and create meaningful impact.'
        },
        {
          icon: 'fa-book',
          title: 'Service Learning',
          detail: 'Integrated service projects that connect classroom learning with real-world community challenges.'
        },
        {
          icon: 'fa-globe',
          title: 'Global Awareness',
          detail: 'Programs that foster cultural understanding and global citizenship among our students.'
        }
      ],
      highlights: [
        'Over 500 students participated in community service this year',
        'Partnerships with 15+ local organizations and NGOs',
        'Monthly community outreach events and initiatives',
        'Student-led volunteer coordination and project management'
      ],
      content: 'Our community involvement programs reflect our commitment to developing socially responsible global citizens. Through structured service learning, students engage with real community challenges while developing leadership skills, empathy, and a sense of civic responsibility. Our partnerships with local organizations ensure that student efforts create meaningful, sustainable impact. From environmental conservation to educational support for underprivileged children, our students are making a difference in the community while learning valuable lessons about collaboration, problem-solving, and social responsibility.'
    },
    'partnerships': {
      title: 'Strategic Partnerships',
      subtitle: 'Collaborations That Strengthen Learning',
      description: 'Updates on strategic partnerships that enhance our educational programs, facilities, and student opportunities.',
      color: '#06b6d4',
      colorLight: '#cffafe',
      icon: 'fa-link',
      images: [newsCard2, newsCard3, newsCard1],
      date: 'March 10, 2026',
      author: 'Administration',
      features: [
        {
          icon: 'fa-university',
          title: 'University Partnerships',
          detail: 'Collaborations with leading universities for advanced learning programs and research opportunities.'
        },
        {
          icon: 'fa-building',
          title: 'Corporate Partnerships',
          detail: 'Industry partnerships providing internships, mentorship, and career development opportunities.'
        },
        {
          icon: 'fa-microscope',
          title: 'Research Initiatives',
          detail: 'Joint research projects with academic institutions advancing educational innovation.'
        },
        {
          icon: 'fa-graduation-cap',
          title: 'Educational Programs',
          detail: 'Specialized programs developed in partnership with educational experts and organizations.'
        }
      ],
      highlights: [
        'Partnership with 8 leading universities for advanced programs',
        'Internship opportunities with 20+ corporate partners',
        'Joint research projects in STEM and innovation',
        'Collaborative curriculum development with educational experts'
      ],
      content: 'Strategic partnerships are central to our mission of providing world-class education. Through collaborations with universities, corporations, and educational organizations, we expand learning opportunities, provide real-world experience, and stay at the forefront of educational innovation. Our partnerships enable students to access advanced programs, internship opportunities, and mentorship from industry professionals. These collaborations also strengthen our facilities, curriculum, and overall educational ecosystem, ensuring our students are prepared for success in a rapidly changing world.'
    },
    'education-trends': {
      title: 'Education Trends',
      subtitle: 'Emerging Learning Approaches & Innovation',
      description: 'Insights on emerging learning approaches, curriculum updates, and global education trends shaping the future of learning.',
      color: '#f59e0b',
      colorLight: '#fef3c7',
      icon: 'fa-lightbulb',
      images: [newsCard1, newsCard2, newsCard3],
      date: 'March 8, 2026',
      author: 'Curriculum Team',
      features: [
        {
          icon: 'fa-brain',
          title: 'Personalized Learning',
          detail: 'Adaptive learning pathways tailored to individual student needs and learning styles.'
        },
        {
          icon: 'fa-network-wired',
          title: 'Digital Integration',
          detail: 'Seamless integration of technology to enhance engagement and accessibility in learning.'
        },
        {
          icon: 'fa-users-gear',
          title: 'Collaborative Learning',
          detail: 'Project-based and peer-learning approaches that develop teamwork and communication skills.'
        },
        {
          icon: 'fa-chart-line',
          title: 'Data-Driven Insights',
          detail: 'Using analytics to understand learning patterns and optimize educational outcomes.'
        }
      ],
      highlights: [
        'Implementation of AI-powered personalized learning systems',
        'Integration of global learning networks and virtual classrooms',
        'Focus on 21st-century skills: creativity, critical thinking, collaboration',
        'Emphasis on social-emotional learning and student wellbeing'
      ],
      content: 'Education is evolving rapidly, and we are committed to staying at the forefront of these changes. Modern education trends emphasize personalized learning, digital integration, and the development of critical thinking and collaboration skills. We continuously update our curriculum and teaching methodologies to reflect these trends, ensuring our students are prepared for a dynamic future. By embracing innovation while maintaining our core values, we create learning experiences that are both engaging and meaningful, preparing students not just for exams, but for life.'
    },
    'student-highlights': {
      title: 'Student Highlights',
      subtitle: 'Achievements, Creativity & Leadership',
      description: 'Stories of student achievements, creativity, leadership, and inspiring moments from our school community.',
      color: '#ec4899',
      colorLight: '#fce7f3',
      icon: 'fa-star',
      images: [newsCard2, newsCard3, newsCard1],
      date: 'March 5, 2026',
      author: 'Student Affairs',
      features: [
        {
          icon: 'fa-trophy',
          title: 'Academic Excellence',
          detail: 'Outstanding achievements in academics, competitions, and scholarly pursuits.'
        },
        {
          icon: 'fa-palette',
          title: 'Creative Expression',
          detail: 'Artistic and creative projects showcasing student talent and innovation.'
        },
        {
          icon: 'fa-handshake',
          title: 'Leadership Impact',
          detail: 'Student leaders making positive changes in school and community.'
        },
        {
          icon: 'fa-heart',
          title: 'Character Development',
          detail: 'Stories of personal growth, resilience, and positive values in action.'
        }
      ],
      highlights: [
        '50+ students recognized for academic excellence this semester',
        'Student-led initiatives impacting 200+ community members',
        'Award-winning performances in arts and athletics',
        'Peer mentorship programs supporting 100+ younger students'
      ],
      content: 'Our students are the heart of our school community. Every day, they demonstrate remarkable achievements, creativity, and leadership. From academic excellence to artistic expression, from community service to personal growth, our students inspire us with their dedication and passion. These stories celebrate not just their accomplishments, but the character, resilience, and values they embody. We are proud to nurture young people who will become thoughtful, compassionate, and innovative leaders in their communities and beyond.'
    },
    'teacher-insights': {
      title: 'Teacher Insights',
      subtitle: 'Perspectives on Teaching & Learning',
      description: 'Perspective pieces from our educators about classroom experiences, teaching innovations, and learning journeys.',
      color: '#10b981',
      colorLight: '#d1fae5',
      icon: 'fa-chalkboard-user',
      images: [newsCard3, newsCard1, newsCard2],
      date: 'March 1, 2026',
      author: 'Faculty',
      features: [
        {
          icon: 'fa-book-open',
          title: 'Innovative Teaching',
          detail: 'Exploring new pedagogies and methods that enhance student engagement and learning.'
        },
        {
          icon: 'fa-comments',
          title: 'Student Connection',
          detail: 'Building meaningful relationships that support student growth and development.'
        },
        {
          icon: 'fa-flask',
          title: 'Continuous Learning',
          detail: 'Teachers as lifelong learners, staying current with educational research and practices.'
        },
        {
          icon: 'fa-lightbulb',
          title: 'Inspiring Curiosity',
          detail: 'Creating classroom environments where questions are encouraged and learning is joyful.'
        }
      ],
      highlights: [
        'Teachers completing 100+ hours of professional development annually',
        'Implementation of 15+ innovative teaching projects this year',
        'Student satisfaction ratings averaging 4.8/5 for teacher effectiveness',
        'Collaborative curriculum development across all departments'
      ],
      content: 'Our teachers are dedicated professionals who go beyond delivering curriculum—they inspire, mentor, and transform lives. Through their insights and experiences, we gain valuable perspectives on what makes learning meaningful and impactful. Our educators continuously innovate, collaborate, and grow, creating classroom environments where students feel supported, challenged, and inspired. Their commitment to excellence and their genuine care for student development are the foundation of our school\'s success.'
    }
  };

  const news = newsData[newsId] || newsData['community-involvement'];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveImageSlide((current) => (current + 1) % news.images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [news.images.length]);

  return (
    <section className="news-detail-page">
      {/* Hero Section */}
      <section className="news-detail-hero" style={{ backgroundColor: news.color }}>
        <div className="news-detail-hero-pattern" />
        <div className="news-detail-hero-image">
          <div className="news-detail-hero-media" aria-hidden="true">
            {news.images.map((image, index) => (
              <div
                key={`${newsId}-${index}`}
                className={`news-detail-slide ${index === activeImageSlide ? 'active' : ''}`}
              >
                <img src={image} alt={news.title} />
              </div>
            ))}
          </div>
          <div className="news-detail-hero-overlay" aria-hidden="true" />
        </div>
        <div className="news-detail-hero-content">
          <button
            className="news-detail-back-btn"
            onClick={() => onNavigate && onNavigate('news-and-updates')}
            aria-label="Back to news"
          >
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="news-detail-hero-icon">
            <i className={`fas ${news.icon}`} />
          </div>
          <h1 className="news-detail-hero-title">{news.title}</h1>
          <p className="news-detail-hero-subtitle">{news.subtitle}</p>
          <div className="news-detail-meta">
            <span className="news-detail-date"><i className="fas fa-calendar" /> {news.date}</span>
            <span className="news-detail-author"><i className="fas fa-user" /> {news.author}</span>
          </div>
        </div>
        <div className="news-detail-hero-dots">
          {news.images.map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              className={`news-detail-dot ${index === activeImageSlide ? 'active' : ''}`}
              onClick={() => setActiveImageSlide(index)}
              aria-label={`Show slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className="news-detail-main">
        {/* About Section */}
        <div className="news-detail-section">
          <div className="news-detail-section-header">
            <h2>About This Update</h2>
            <div className="news-detail-section-line" style={{ backgroundColor: news.color }} />
          </div>
          <p className="news-detail-description">{news.description}</p>
          <div className="news-detail-highlight-box" style={{ backgroundColor: news.colorLight, borderLeftColor: news.color }}>
            <i className={`fas ${news.icon}`} style={{ color: news.color }} />
            <p>{news.content}</p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="news-detail-section">
          <div className="news-detail-section-header">
            <h2>Key Highlights</h2>
            <div className="news-detail-section-line" style={{ backgroundColor: news.color }} />
          </div>
          <div className="news-features-grid">
            {news.features.map((feature, index) => (
              <div key={`feature-${index}`} className="news-feature-card" style={{ borderTopColor: news.color }}>
                <div 
                  className="news-feature-icon" 
                  style={{ 
                    '--feature-color': news.color,
                    '--feature-color-dark': news.color + 'cc'
                  }}
                >
                  <i className={`fas ${feature.icon}`} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlights */}
        <div className="news-detail-section">
          <div className="news-detail-section-header">
            <h2>By The Numbers</h2>
            <div className="news-detail-section-line" style={{ backgroundColor: news.color }} />
          </div>
          <div className="news-highlights-grid">
            {news.highlights.map((highlight, index) => (
              <div key={`highlight-${index}`} className="news-highlight-card" style={{ backgroundColor: news.colorLight, borderLeftColor: news.color }}>
                <div className="news-highlight-icon" style={{ color: news.color }}>
                  <i className="fas fa-check-circle" />
                </div>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="news-detail-cta" 
        style={{ 
          '--cta-color': news.color,
          '--cta-color-dark': news.color + 'cc'
        }}
      >
        <div className="news-detail-cta-content">
          <h2>Learn More About Our Initiatives</h2>
          <p>Discover how we're making a difference in our community and beyond.</p>
          <button className="news-detail-cta-btn" onClick={() => onNavigate && onNavigate('contact')}>
            Get Involved <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default NewsDetailPage;
